import React, { useEffect, useState } from "react";
import { Table, Button, Form, Spinner, InputGroup, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ModalConfirmar from "../../components/ModalConfirmar";
import PaginacionSimple from "../../components/PaginacionSimple";
import SelectorCuenta, { TOTAL_CUENTA_ID } from "../../components/SelectorCuenta";
import useCuentasCliente from "../../hooks/useCuentasCliente";
import { useAuth } from "../../auth/AuthContext";
import * as ingresosApi from "../../api/ingresos";
import { formatoNumero } from "../../utils/moneda";

export default function Ingresos() {
  const navigate = useNavigate();
  const { esAdministrador } = useAuth();
  const { cuentas: todasLasCuentas, cuentaId, setCuentaId, cargando: cargandoCuentas, recargar } = useCuentasCliente();
  // Los Ingresos no aplican a Tarjetas de Crédito.
  const cuentas = todasLasCuentas.filter((c) => c.tipo !== "TarjetaCredito");
  const [ingresos, setIngresos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [monto, setMonto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);
  const esTotal = cuentaId === TOTAL_CUENTA_ID;

  const cargar = async (paginaAConsultar = pagina) => {
    if (!cuentaId) return;
    setCargando(true);
    try {
      if (esTotal) {
        const listas = await Promise.all(cuentas.map((c) => ingresosApi.listarIngresos(c.cuentaId)));
        setIngresos(listas.flatMap((r) => r.items).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
        setTotalPaginas(1);
      } else {
        const resultado = await ingresosApi.listarIngresos(cuentaId, paginaAConsultar);
        setIngresos(resultado.items);
        setTotalPaginas(Math.max(1, Math.ceil(resultado.total / resultado.tamanoPagina)));
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { setPagina(1); cargar(1); }, [cuentaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarPagina = (p) => { setPagina(p); cargar(p); };

  const handleAgregar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await ingresosApi.registrarIngreso({ cuentaId, motivo, monto: Number(monto) });
      setMotivo(""); setMonto("");
      setPagina(1);
      await cargar(1);
      recargar(false);
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    await ingresosApi.eliminarIngreso(aEliminar.ingresoId);
    setAEliminar(null);
    await cargar();
    recargar(false);
  };

  if (cargandoCuentas) return <Spinner animation="border" />;
  if (!cuentas.length) {
    return <Alert variant="info">Primero crea una cuenta bancaria en el módulo "Cuentas Bancarias".</Alert>;
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-1 gap-2">
        <h4 className="mb-0">Ingresos</h4>
        {!esTotal && (
          <Button size="sm" variant="outline-secondary" onClick={() => navigate("/historicos")}>
            <i className="bi bi-clock-history me-1"></i>Ver históricos
          </Button>
        )}
      </div>
      <p className="text-muted small">Nómina, sueldo, bonos y demás entradas de dinero a la cuenta. Se muestran los últimos 2 meses — para rangos más antiguos usa "Ver históricos".</p>

      <SelectorCuenta cuentas={cuentas} cuentaId={cuentaId} onChange={setCuentaId} />

      {!esAdministrador && !esTotal && (
        <Form onSubmit={handleAgregar} className="fs-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-6">
              <Form.Label className="small mb-1">Motivo</Form.Label>
              <Form.Control value={motivo} onChange={(e) => setMotivo(e.target.value)} required placeholder="Ej. Nómina, sueldo, bono..." />
            </div>
            <div className="col-8 col-sm-4">
              <Form.Label className="small mb-1">Monto</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control type="number" step="0.01" min="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} required />
              </InputGroup>
            </div>
            <div className="col-4 col-sm-2">
              <Button type="submit" variant="primary" className="w-100" disabled={guardando}>
                {guardando ? <Spinner size="sm" animation="border" /> : "Agregar"}
              </Button>
            </div>
          </div>
        </Form>
      )}

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead>
              <tr><th>Motivo</th><th>Fecha</th><th className="text-end">Monto</th><th></th></tr>
            </thead>
            <tbody>
              {ingresos.map((i) => (
                <tr key={i.ingresoId}>
                  <td data-label="Motivo">{i.motivo}</td>
                  <td data-label="Fecha">{new Date(i.fecha).toLocaleDateString("es-ES")}</td>
                  <td data-label="Monto" className="text-end text-fs-ingreso fw-semibold">+${formatoNumero(i.monto)}</td>
                  <td data-label="Acciones" className="text-end">
                    {!esAdministrador && (
                      <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(i)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!ingresos.length && (
                <tr><td colSpan={4} className="text-center text-muted py-4">No hay ingresos registrados.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!esTotal && <PaginacionSimple pagina={pagina} totalPaginas={totalPaginas} onChange={cambiarPagina} />}

      <ModalConfirmar
        show={!!aEliminar}
        titulo="Eliminar ingreso"
        mensaje={`¿Eliminar el ingreso "${aEliminar?.motivo}"? Esto revertirá el monto del saldo.`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}
